import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, Eye, EyeOff, Building2, UserCircle, ArrowLeft, ArrowRight, Shield } from 'lucide-react';
import { registerInstitution, checkEmailAvailability, checkInstitutionCodeAvailability } from '../../services/authService';

/**
 * Institution Registration Flow - Multi-Step Wizard
 * Step 1: Institution Details
 * Step 2: Admin Teacher Account
 * Step 3: Success (auto-redirect)
 */

const STORAGE_KEY = 'institutionRegForm';

// Password strength calculator
const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: 'bg-gray-200' };

  let score = 0;
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  const strengths = [
    { score: 0, label: '', color: 'bg-gray-200' },
    { score: 1, label: 'Very Weak', color: 'bg-red-500' },
    { score: 2, label: 'Weak', color: 'bg-orange-500' },
    { score: 3, label: 'Fair', color: 'bg-yellow-500' },
    { score: 4, label: 'Good', color: 'bg-lime-500' },
    { score: 5, label: 'Strong', color: 'bg-green-500' },
    { score: 6, label: 'Very Strong', color: 'bg-emerald-600' }
  ];

  return strengths[Math.min(score, 6)];
};

const InstitutionRegistration = ({ onSuccess, onBack, onNavigate }) => {
  // Multi-step wizard
  const [step, setStep] = useState(1);
  const [isSuccess, setIsSuccess] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    institutionName: '',
    institutionCode: '',
    institutionEmail: '',
    teacherFullName: '',
    teacherEmail: '',
    password: '',
    confirmPassword: ''
  });

  // Validation states
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailAvailable, setEmailAvailable] = useState(null);
  const [codeChecking, setCodeChecking] = useState(false);
  const [codeAvailable, setCodeAvailable] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Password strength
  const passwordStrength = getPasswordStrength(formData.password);
  const passwordsMatch = formData.password && formData.confirmPassword &&
                         formData.password === formData.confirmPassword;

  // Load saved form data from localStorage
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const data = JSON.parse(saved);
        // Don't restore passwords for security
        setFormData(prev => ({
          ...prev,
          institutionName: data.institutionName || '',
          institutionCode: data.institutionCode || '',
          institutionEmail: data.institutionEmail || '',
          teacherFullName: data.teacherFullName || '',
          teacherEmail: data.teacherEmail || ''
        }));
      } catch (e) {
        console.error('[InstitutionReg] Failed to load saved form:', e);
      }
    }
  }, []);

  // Save form data to localStorage (exclude passwords)
  useEffect(() => {
    const dataToSave = {
      institutionName: formData.institutionName,
      institutionCode: formData.institutionCode,
      institutionEmail: formData.institutionEmail,
      teacherFullName: formData.teacherFullName,
      teacherEmail: formData.teacherEmail
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
  }, [formData.institutionName, formData.institutionCode, formData.institutionEmail,
      formData.teacherFullName, formData.teacherEmail]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');

    // Reset availability checks when values change
    if (name === 'teacherEmail') {
      setEmailAvailable(null);
    }
    if (name === 'institutionCode') {
      setCodeAvailable(null);
    }
  };

  const handleEmailBlur = async () => {
    if (!formData.teacherEmail.trim()) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.teacherEmail)) return;

    setEmailChecking(true);
    const result = await checkEmailAvailability(formData.teacherEmail);
    setEmailChecking(false);

    if (result.available) {
      setEmailAvailable(true);
    } else if (result.taken) {
      setEmailAvailable(false);
      setError('Email already registered. Please login instead.');
    } else {
      setError(result.error || 'Failed to check email');
    }
  };

  const handleCodeBlur = async () => {
    if (!formData.institutionCode.trim()) return;

    setCodeChecking(true);
    const result = await checkInstitutionCodeAvailability(formData.institutionCode);
    setCodeChecking(false);

    if (result.available) {
      setCodeAvailable(true);
    } else if (result.taken) {
      setCodeAvailable(false);
      setError('Institution code already taken. Please choose another one.');
    } else {
      setError(result.error || 'Failed to check institution code');
    }
  };

  const validateStep1 = () => {
    if (!formData.institutionName.trim() || formData.institutionName.length < 3) {
      setError('Institution name must be at least 3 characters');
      return false;
    }

    if (!formData.institutionCode.trim() || formData.institutionCode.length < 3) {
      setError('Institution code must be at least 3 characters');
      return false;
    }

    if (!/^[A-Z0-9_-]+$/.test(formData.institutionCode)) {
      setError('Institution code can only contain uppercase letters, numbers, hyphens, and underscores');
      return false;
    }

    if (!codeAvailable) {
      setError('Please verify the institution code is available');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.institutionEmail)) {
      setError('Please enter a valid institution email');
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    if (!formData.teacherFullName.trim() || formData.teacherFullName.length < 3) {
      setError('Your name must be at least 3 characters');
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.teacherEmail)) {
      setError('Please enter a valid email address');
      return false;
    }

    if (!emailAvailable) {
      setError('Please verify your email is available');
      return false;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }

    if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      setError('Password must contain uppercase, lowercase, and a number');
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }

    return true;
  };

  const handleNextStep = () => {
    setError('');
    if (step === 1 && validateStep1()) {
      setStep(2);
    }
  };

  const handlePrevStep = () => {
    setError('');
    setStep(1);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateStep2()) return;

    setRegistering(true);

    const result = await registerInstitution({
      institutionName: formData.institutionName.trim(),
      institutionCode: formData.institutionCode.trim().toUpperCase(),
      institutionEmail: formData.institutionEmail.trim().toLowerCase(),
      teacherFullName: formData.teacherFullName.trim(),
      teacherEmail: formData.teacherEmail.trim().toLowerCase(),
      password: formData.password
    });

    setRegistering(false);

    if (result.success) {
      // Clear saved form data
      localStorage.removeItem(STORAGE_KEY);

      // Show success state
      setIsSuccess(true);

      // Redirect after animation
      setTimeout(() => {
        if (onSuccess) {
          onSuccess(result.teacher, result.session);
        }
      }, 2000);
    } else {
      setError(result.error || 'Registration failed. Please try again.');
    }
  };

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('landing');
    } else if (onBack) {
      onBack();
    }
  };

  // Animated availability feedback component
  const AvailabilityFeedback = ({ checking, available, availableText, takenText }) => (
    <AnimatePresence mode="wait">
      {checking && (
        <motion.div
          key="checking"
          initial={{ opacity: 0, y: -5 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -5 }}
          className="flex items-center gap-1.5 mt-1.5"
        >
          <Loader2 className="w-3.5 h-3.5 text-gray-400 animate-spin" />
          <span className="text-xs text-gray-500">Checking availability...</span>
        </motion.div>
      )}
      {!checking && available === true && (
        <motion.div
          key="available"
          initial={{ opacity: 0, y: -5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5 }}
          className="flex items-center gap-1.5 mt-1.5"
        >
          <CheckCircle className="w-3.5 h-3.5 text-green-500" />
          <span className="text-xs text-green-600 font-medium">{availableText}</span>
        </motion.div>
      )}
      {!checking && available === false && (
        <motion.div
          key="taken"
          initial={{ opacity: 0, y: -5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -5 }}
          className="flex items-center gap-1.5 mt-1.5"
        >
          <XCircle className="w-3.5 h-3.5 text-red-500" />
          <span className="text-xs text-red-600 font-medium">{takenText}</span>
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Success screen
  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-blue-50 to-purple-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-md w-full"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
          >
            <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
          </motion.div>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-2xl font-bold text-gray-900 mb-2"
          >
            Welcome to Fluence!
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-gray-600 mb-4"
          >
            Your institution has been created successfully.
          </motion.p>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="flex items-center justify-center gap-2 text-blue-600"
          >
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Redirecting to dashboard...</span>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg"
      >
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-white/80 hover:text-white mb-4 text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </button>
            <h1 className="text-2xl md:text-3xl font-bold mb-1">
              Register Your Institution
            </h1>
            <p className="text-white/80 text-sm">
              Create your account and start your 30-day free trial
            </p>

            {/* Progress Indicator */}
            <div className="mt-6 flex items-center gap-2">
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                step >= 1 ? 'bg-white text-blue-600' : 'bg-white/20 text-white/60'
              }`}>
                <Building2 className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Institution</span>
                <span className="sm:hidden">1</span>
              </div>
              <div className={`w-8 h-0.5 ${step >= 2 ? 'bg-white' : 'bg-white/30'}`} />
              <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                step >= 2 ? 'bg-white text-purple-600' : 'bg-white/20 text-white/60'
              }`}>
                <UserCircle className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Admin Account</span>
                <span className="sm:hidden">2</span>
              </div>
            </div>
          </div>

          {/* Form Content */}
          <form onSubmit={handleRegister} className="p-6">
            <AnimatePresence mode="wait">
              {/* Step 1: Institution Details */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-5"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Institution Details</h2>
                  </div>

                  {/* Institution Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Institution Name
                    </label>
                    <input
                      type="text"
                      name="institutionName"
                      value={formData.institutionName}
                      onChange={handleFormChange}
                      placeholder="e.g., St. Mary's High School"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-base"
                      autoFocus
                    />
                  </div>

                  {/* Institution Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Institution Code
                      <span className="text-gray-400 font-normal ml-1">(Unique ID)</span>
                    </label>
                    <input
                      type="text"
                      name="institutionCode"
                      value={formData.institutionCode}
                      onChange={(e) => handleFormChange({
                        target: { name: 'institutionCode', value: e.target.value.toUpperCase() }
                      })}
                      onBlur={handleCodeBlur}
                      placeholder="e.g., STMARYS-CLASS6-2025"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors font-mono text-base"
                    />
                    <AvailabilityFeedback
                      checking={codeChecking}
                      available={codeAvailable}
                      availableText="Code is available!"
                      takenText="Code already taken"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Students will use this code to join your institution
                    </p>
                  </div>

                  {/* Institution Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Official Institution Email
                    </label>
                    <input
                      type="email"
                      name="institutionEmail"
                      value={formData.institutionEmail}
                      onChange={handleFormChange}
                      placeholder="e.g., principal@stmarys.edu"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors text-base"
                    />
                    <p className="text-xs text-gray-500 mt-1.5">
                      Official contact email for the institution
                    </p>
                  </div>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Next Button */}
                  <button
                    type="button"
                    onClick={handleNextStep}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2 text-base"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </motion.div>
              )}

              {/* Step 2: Admin Account */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <UserCircle className="w-5 h-5 text-purple-600" />
                    <h2 className="text-lg font-semibold text-gray-900">Admin Account</h2>
                  </div>

                  {/* Full Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Full Name
                    </label>
                    <input
                      type="text"
                      name="teacherFullName"
                      value={formData.teacherFullName}
                      onChange={handleFormChange}
                      placeholder="e.g., Dr. Rajesh Kumar"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors text-base"
                      autoFocus
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Your Email
                      <span className="text-gray-400 font-normal ml-1">(Login ID)</span>
                    </label>
                    <input
                      type="email"
                      name="teacherEmail"
                      value={formData.teacherEmail}
                      onChange={handleFormChange}
                      onBlur={handleEmailBlur}
                      placeholder="e.g., rajesh.kumar@stmarys.edu"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors text-base"
                    />
                    <AvailabilityFeedback
                      checking={emailChecking}
                      available={emailAvailable}
                      availableText="Email is available!"
                      takenText="Email already registered"
                    />
                  </div>

                  {/* Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Create Password
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        name="password"
                        value={formData.password}
                        onChange={handleFormChange}
                        placeholder="Min. 8 characters"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:outline-none transition-colors pr-12 text-base"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>

                    {/* Password Strength Indicator */}
                    {formData.password && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2"
                      >
                        <div className="flex gap-1 mb-1">
                          {[1, 2, 3, 4, 5, 6].map((i) => (
                            <div
                              key={i}
                              className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${
                                i <= passwordStrength.score ? passwordStrength.color : 'bg-gray-200'
                              }`}
                            />
                          ))}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Shield className={`w-3 h-3 ${
                            passwordStrength.score >= 4 ? 'text-green-500' : 'text-gray-400'
                          }`} />
                          <span className={`text-xs font-medium ${
                            passwordStrength.score >= 4 ? 'text-green-600' :
                            passwordStrength.score >= 2 ? 'text-yellow-600' : 'text-red-600'
                          }`}>
                            {passwordStrength.label || 'Enter password'}
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Confirm Password
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        name="confirmPassword"
                        value={formData.confirmPassword}
                        onChange={handleFormChange}
                        placeholder="Re-enter password"
                        className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none transition-colors pr-12 text-base ${
                          formData.confirmPassword
                            ? passwordsMatch
                              ? 'border-green-300 focus:border-green-500'
                              : 'border-red-300 focus:border-red-500'
                            : 'border-gray-200 focus:border-purple-500'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                      >
                        {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                    {formData.confirmPassword && (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="flex items-center gap-1.5 mt-1.5"
                      >
                        {passwordsMatch ? (
                          <>
                            <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                            <span className="text-xs text-green-600 font-medium">Passwords match</span>
                          </>
                        ) : (
                          <>
                            <XCircle className="w-3.5 h-3.5 text-red-500" />
                            <span className="text-xs text-red-600 font-medium">Passwords don't match</span>
                          </>
                        )}
                      </motion.div>
                    )}
                  </div>

                  {/* Error */}
                  <AnimatePresence>
                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm flex items-center gap-2"
                      >
                        <XCircle className="w-4 h-4 flex-shrink-0" />
                        {error}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Buttons */}
                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold py-3.5 px-6 rounded-xl transition-colors flex items-center justify-center gap-2"
                    >
                      <ArrowLeft className="w-4 h-4" />
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={registering || !emailAvailable || !passwordsMatch}
                      className="flex-[2] bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold py-3.5 px-6 rounded-xl transition-all disabled:from-gray-300 disabled:to-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {registering ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating...
                        </>
                      ) : (
                        <>
                          Create Institution
                          <CheckCircle className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Footer */}
            <p className="text-xs text-gray-500 text-center mt-6">
              By registering, you agree to our Terms of Service and Privacy Policy
            </p>
          </form>
        </div>

        {/* Already have account */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="mt-4 text-center"
        >
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <button
              type="button"
              onClick={() => onNavigate ? onNavigate('teacher-login') : null}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Login here
            </button>
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default InstitutionRegistration;
